// @ts-check
import { useEffect, useState } from 'react';
import SearchInput from '../SearchInput';
import isFunction from 'lodash/isFunction';
import { FixedSizeList as List } from 'react-window';
import deburr from 'lodash/deburr';
import { Loading, LoadingItem, LOADING_MORE_ITEMS_COUNT } from './loaders';
import useListScrollToBottom from 'components/hooks/useListScrollToBottom';

const ListItem = ({ style, data, index }) => {
  const { items, isLoadingMore } = data;
  const item = items?.[index];

  // If this is a loading item (beyond the actual items)
  if (isLoadingMore && index >= items.length) {
    return <LoadingItem style={style} />;
  }

  if (!item) {
    return null;
  }

  return (
    <div
      className='cursor-pointer p-1 flex flex-row text-xs overflow-hidden'
      style={style}
    >
      {item.view}
    </div>
  );
};

export const SearchableList = ({
  placeholder,
  items,
  searchKeys,
  emptyState,
  searchKeyword,
  setSearchKeyword,
  isLoading = false,
  isLoadingMore = false,
  onScrollToBottom
}) => {
  const isControlled =
    searchKeyword !== undefined && setSearchKeyword !== undefined;

  // State for uncontrolled mode
  const [displayedItems, setDisplayedItems] = useState(items);
  const [searchValue, setSearchValue] = useState('');

  // Use controlled or uncontrolled values
  const currentSearchValue = isControlled ? searchKeyword : searchValue;
  const currentItems = isControlled ? items : displayedItems;

  const { handleScroll, listRef } = useListScrollToBottom({
    callback: onScrollToBottom,
    enabled: !isLoading && !isLoadingMore,
    items: currentItems
  });

  useEffect(() => {
    if (!isControlled) {
      setDisplayedItems(items);
      setSearchValue('');
    }
  }, [items, isControlled]);

  const onSearch = input => {
    if (isControlled) {
      setSearchKeyword(input);
      return;
    }

    setSearchValue(input);
    const filteredItems = items.filter(
      item =>
        !!searchKeys
          .map(id => {
            const keyValue = deburr(item[id]) || '';
            return keyValue
              .toString()
              .toLowerCase()
              .includes(deburr(input).toLowerCase());
          })
          .filter(Boolean).length
    );

    setDisplayedItems(filteredItems);
  };

  // Calculate total item count including loading items
  const totalItemCount =
    currentItems.length + (isLoadingMore ? LOADING_MORE_ITEMS_COUNT : 0);

  return (
    <div className='box-border flex size-full flex-col rounded-sm border border-slate-200 p-2'>
      <div className='-mx-2 mb-3 box-border w-full px-4'>
        <SearchInput
          value={currentSearchValue}
          onChange={onSearch}
          placeholder={placeholder}
          height='3.3rem'
        />
      </div>

      {isLoading ? (
        <Loading />
      ) : !currentItems.length ? (
        <>
          {isFunction(emptyState) ? emptyState(currentSearchValue) : emptyState}
        </>
      ) : (
        /* @ts-ignore */
        <List
          ref={listRef}
          height={300}
          itemData={{ items: currentItems, isLoadingMore }}
          itemCount={totalItemCount}
          itemSize={40}
          onScroll={handleScroll}
        >
          {ListItem}
        </List>
      )}
    </div>
  );
};
